import React, { useEffect, useMemo, useState } from "react";
import SectionCard from "../components/SectionCard";
import { Input, Select } from "../components/FormControls";
import {
  CalendarDays,
  Clock,
  Plus,
  RefreshCw,
  Save,
  Trash2,
} from "lucide-react";
import { toast } from "react-toastify";

import useOrderSettings from "../../../Services/OrderServices/OrderSettingsServices/useOrderSettings";

const DAYS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const SHIFTS = ["1", "2", "3"];

const makeEmptyRow = (day = "MONDAY", shift = "1") => ({
  pk_timing_id: "0",
  restaurant_id: "",
  day_name: day,
  shift_no: shift,
  open_time: "09:00",
  close_time: "23:00",
  is_closed: "0",
  is_24_hours: "0",
  created_at: "",
  updated_at: "",
});

const parseBoolTo01 = (value) => {
  const v = String(value ?? "").toLowerCase();

  if (v === "true" || v === "1" || v === "yes") return "1";
  return "0";
};

const to12HourLabel = (value) => {
  if (!value) return "—";

  const str = String(value).trim();

  if (
    str.toUpperCase().includes("AM") ||
    str.toUpperCase().includes("PM")
  ) {
    return str;
  }

  const [hourRaw, minuteRaw] = str.split(":");
  let hour = Number(hourRaw);
  const minute = minuteRaw || "00";

  if (Number.isNaN(hour)) return str;

  const suffix = hour >= 12 ? "PM" : "AM";

  hour = hour % 12;
  if (hour === 0) hour = 12;

  return `${String(hour).padStart(2, "0")}:${minute} ${suffix}`;
};

const toTimeInput = (value) => {
  if (!value) return "";

  const str = String(value).trim();

  const ampmMatch = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

  if (ampmMatch) {
    let hour = Number(ampmMatch[1]);
    const minute = ampmMatch[2];
    const suffix = ampmMatch[3].toUpperCase();

    if (suffix === "PM" && hour !== 12) hour += 12;
    if (suffix === "AM" && hour === 12) hour = 0;

    return `${String(hour).padStart(2, "0")}:${minute}`;
  }

  if (str.length >= 5) return str.slice(0, 5);

  return str;
};

const toApiTime = (value) => {
  if (!value) return "00:00:00";
  if (String(value).length === 5) return `${value}:00`;
  return value;
};

const normalizeRow = (row) => ({
  pk_timing_id: String(row?.pk_timing_id || row?.id || "0"),
  restaurant_id: String(row?.restaurant_id || ""),
  day_name: String(row?.day_name || row?.dayName || "MONDAY").toUpperCase(),
  shift_no: String(row?.shift_no || row?.shiftNo || "1"),
  open_time: toTimeInput(row?.open_time || row?.openTime || "09:00 AM"),
  close_time: toTimeInput(row?.close_time || row?.closeTime || "11:00 PM"),
  is_closed: parseBoolTo01(row?.is_closed ?? row?.isClosed ?? "False"),
  is_24_hours: parseBoolTo01(row?.is_24_hours ?? row?.is24Hours ?? "False"),
  created_at: row?.created_at || "",
  updated_at: row?.updated_at || "",
});

const uniqueLatestTimings = (list = []) => {
  const map = new Map();

  list.forEach((item) => {
    const row = normalizeRow(item);
    const key = `${row.day_name}-${row.shift_no}`;
    const existing = map.get(key);

    if (!existing) {
      map.set(key, row);
      return;
    }

    const existingId = Number(existing.pk_timing_id || 0);
    const currentId = Number(row.pk_timing_id || 0);

    if (currentId >= existingId) {
      map.set(key, row);
    }
  });

  return Array.from(map.values()).sort((a, b) => {
    const dayA = DAYS.indexOf(a.day_name);
    const dayB = DAYS.indexOf(b.day_name);

    if (dayA !== dayB) return dayA - dayB;
    return Number(a.shift_no) - Number(b.shift_no);
  });
};

export default function OrderTimingSection({ userID }) {
  const { getRestaurantTimingsV2, saveRestaurantTimingsV2 } =
    useOrderSettings();

  /**
   * For now static restaurant id as discussed.
   * Later live:
   * const restaurantId = userID;
   */
  const restaurantId = userID;

  const [timings, setTimings] = useState([]);
  const [rows, setRows] = useState([makeEmptyRow()]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const groupedTimings = useMemo(() => {
    const map = {};

    DAYS.forEach((day) => {
      map[day] = [];
    });

    timings.forEach((item) => {
      const row = normalizeRow(item);
      const day = row.day_name;

      if (!map[day]) map[day] = [];
      map[day].push(row);
    });

    Object.keys(map).forEach((day) => {
      map[day].sort((a, b) => Number(a.shift_no) - Number(b.shift_no));
    });

    return map;
  }, [timings]);

  const load = async () => {
    if (!restaurantId) return;

    setLoading(true);

    try {
      const result = await getRestaurantTimingsV2(restaurantId);

      if (result.success) {
        const list = Array.isArray(result.data) ? result.data : [];
        const uniqueList = uniqueLatestTimings(list);

        setTimings(uniqueList);

        if (uniqueList.length > 0) {
          setRows(uniqueList);
        } else {
          setRows([makeEmptyRow()]);
        }
      } else {
        setTimings([]);
        setRows([makeEmptyRow()]);
        toast.error(result.message || "Failed to fetch timings");
      }
    } catch (error) {
      console.error("load restaurant timings error:", error);
      setTimings([]);
      setRows([makeEmptyRow()]);
      toast.error("Failed to fetch timings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID]);

  const updateRow = (index, key, value) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;

        const next = {
          ...row,
          [key]: value,
        };

        if (key === "is_closed" && value === "1") {
          next.is_24_hours = "0";
          next.open_time = "00:00";
          next.close_time = "00:00";
        }

        if (key === "is_closed" && value === "0") {
          next.open_time = row.open_time === "00:00" ? "09:00" : row.open_time;
          next.close_time =
            row.close_time === "00:00" ? "23:00" : row.close_time;
        }

        if (key === "is_24_hours" && value === "1") {
          next.is_closed = "0";
          next.open_time = "00:00";
          next.close_time = "23:59";
        }

        if (key === "is_24_hours" && value === "0") {
          next.open_time = row.open_time === "00:00" ? "09:00" : row.open_time;
          next.close_time =
            row.close_time === "23:59" ? "23:00" : row.close_time;
        }

        return next;
      }),
    );
  };

  const addRow = () => {
    const last = rows[rows.length - 1];
    const sameDayRows = rows.filter((r) => r.day_name === last?.day_name);
    const nextShift = String(Math.min(sameDayRows.length + 1, 3));

    setRows((prev) => [
      ...prev,
      makeEmptyRow(last?.day_name || "MONDAY", nextShift),
    ]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) {
      toast.error("At least one timing row is required");
      return;
    }

    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const validateRows = () => {
    const used = new Set();

    for (const row of rows) {
      const key = `${row.day_name}-${row.shift_no}`;

      if (used.has(key)) {
        toast.error(`${row.day_name} shift ${row.shift_no} is added twice`);
        return false;
      }

      used.add(key);

      if (row.is_closed === "1") continue;
      if (row.is_24_hours === "1") continue;

      if (!row.open_time || !row.close_time) {
        toast.error(`Please add open and close time for ${row.day_name}`);
        return false;
      }

      if (row.open_time >= row.close_time) {
        toast.error(
          `${row.day_name} shift ${row.shift_no}: close time must be after open time`,
        );
        return false;
      }
    }

    return true;
  };

  const save = async () => {
    if (!validateRows()) return;

    setSaving(true);

    try {
      const requests = rows.map((row) => ({
        pk_timing_id: String(row.pk_timing_id || "0"),
        day_name: row.day_name,
        shift_no: String(row.shift_no),
        open_time:
          row.is_closed === "1" ? "00:00:00" : toApiTime(row.open_time),
        close_time:
          row.is_closed === "1" ? "00:00:00" : toApiTime(row.close_time),
        is_closed: row.is_closed === "1" ? "1" : "0",
        is_24_hours: row.is_24_hours === "1" ? "1" : "0",
      }));

      const result = await saveRestaurantTimingsV2({
        restaurantId,
        requests,
      });

      if (result.success) {
        toast.success(result.message || "Timings saved successfully");
        load();
      } else {
        toast.error(result.message || "Failed to save timings");
      }
    } catch (error) {
      console.error("save restaurant timings error:", error);
      toast.error("Failed to save timings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <SectionCard icon={CalendarDays} title="Restaurant Timing Overview">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Weekly opening schedule
            </p>
            <p className="text-xs text-gray-500">
              Manage day-wise shifts, closed days and 24 hours availability.
            </p>
          </div>

          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {DAYS.map((day) => {
            const dayRows = groupedTimings[day] || [];
            const isClosed =
              dayRows.length > 0 && dayRows.every((r) => r.is_closed === "1");

            return (
              <div
                key={day}
                className="rounded-2xl border border-gray-100 bg-gray-50 p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-bold text-gray-900">{day}</div>

                  {dayRows.length === 0 ? (
                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500">
                      Not Set
                    </span>
                  ) : isClosed ? (
                    <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600">
                      Closed
                    </span>
                  ) : (
                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                      Open
                    </span>
                  )}
                </div>

                {loading ? (
                  <div className="rounded-xl bg-white p-3 text-sm text-gray-500">
                    Loading...
                  </div>
                ) : dayRows.length === 0 ? (
                  <div className="rounded-xl bg-white p-3 text-sm text-gray-500">
                    No timing added
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dayRows.map((item, index) => (
                      <div
                        key={`${day}-${item.shift_no}-${item.pk_timing_id}-${index}`}
                        className="flex items-center justify-between rounded-xl bg-white px-3 py-2"
                      >
                        <div>
                          <div className="text-xs font-semibold text-gray-500">
                            Shift {item.shift_no}
                          </div>

                          <div className="text-sm font-bold text-gray-900">
                            {item.is_closed === "1"
                              ? "Closed"
                              : item.is_24_hours === "1"
                                ? "24 Hours"
                                : `${to12HourLabel(
                                  item.open_time,
                                )} - ${to12HourLabel(item.close_time)}`}
                          </div>
                        </div>

                        <Clock size={16} className="text-gray-400" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard icon={Clock} title="Update Restaurant Timings">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Add multiple timing rows
            </p>
            <p className="text-xs text-gray-500">
              You can save multiple days and shifts in one API call.
            </p>
          </div>

          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black"
          >
            <Plus size={16} />
            Add Row
          </button>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#01C37D] text-white">
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    Day
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    Shift
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    Open Time
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    Close Time
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    Closed
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 font-semibold">
                    24 Hours
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-right font-semibold">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {rows.map((row, index) => {
                  const disabledTime =
                    row.is_closed === "1" || row.is_24_hours === "1";

                  return (
                    <tr key={`${row.pk_timing_id}-${index}`} className="hover:bg-gray-50">
                      <td className="min-w-[170px] px-4 py-3">
                        <Select
                          value={row.day_name}
                          onChange={(e) =>
                            updateRow(index, "day_name", e.target.value)
                          }
                        >
                          {DAYS.map((day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </Select>
                      </td>

                      <td className="min-w-[110px] px-4 py-3">
                        <Select
                          value={row.shift_no}
                          onChange={(e) =>
                            updateRow(index, "shift_no", e.target.value)
                          }
                        >
                          {SHIFTS.map((shift) => (
                            <option key={shift} value={shift}>
                              Shift {shift}
                            </option>
                          ))}
                        </Select>
                      </td>

                      <td className="min-w-[140px] px-4 py-3">
                        <Input
                          type="time"
                          value={row.open_time}
                          disabled={disabledTime}
                          onChange={(e) =>
                            updateRow(index, "open_time", e.target.value)
                          }
                        />
                      </td>

                      <td className="min-w-[140px] px-4 py-3">
                        <Input
                          type="time"
                          value={row.close_time}
                          disabled={disabledTime}
                          onChange={(e) =>
                            updateRow(index, "close_time", e.target.value)
                          }
                        />
                      </td>

                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            updateRow(
                              index,
                              "is_closed",
                              row.is_closed === "1" ? "0" : "1",
                            )
                          }
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${row.is_closed === "1"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-600"
                            }`}
                        >
                          {row.is_closed === "1" ? "Yes" : "No"}
                        </button>
                      </td>

                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() =>
                            updateRow(
                              index,
                              "is_24_hours",
                              row.is_24_hours === "1" ? "0" : "1",
                            )
                          }
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${row.is_24_hours === "1"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-gray-100 text-gray-600"
                            }`}
                        >
                          {row.is_24_hours === "1" ? "Yes" : "No"}
                        </button>
                      </td>

                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => removeRow(index)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-600 hover:bg-red-100"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-gray-500">
            API sends <b>0</b> for false and <b>1</b> for true. Time is saved in
            24-hour format.
          </div>

          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save size={16} />
            {saving ? "Saving..." : "Save Timings"}
          </button>
        </div>
      </SectionCard>
    </div>
  );
}